// hooks/useBodyScrollLock.js
import { useEffect } from "react";

/**
 * This hook locks the whole page (body) scroll while `enabled` is true.
 * It uses iOS-safe fixed-position strategy on iOS.
 * It uses overflow-hidden + scrollbar compensation to avoid layout shift.
 */
export default function useBodyScrollLock(enabled) {
  useEffect(() => {
    if (!enabled) return;

    const body = document.body;
    const docEl = document.documentElement;

    // iOS detection (covers iPhone/iPad Safari + WKWebView)
    const ua = navigator.userAgent || "";
    const isiOS =
      /iP(ad|hone|od)/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    // Save inline styles to restore on cleanup
    const prev = {
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };

    /**
     * Measure scrollbar width (0 on overlay scrollbars), if it exists
     * This is used to calculate how much padding to add to the page when the scroll bar disappears
     * This is so that the content on the page does not shift horizontally
     */
    const scrollBarWidth = window.innerWidth - docEl.clientWidth;

    // Remember current scroll position
    const startScrollY = window.scrollY;

    if (isiOS) {
      // Lock by fixing body at current scroll offset
      body.style.position = "fixed";
      body.style.top = `-${startScrollY}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100%";
    } else {
      body.style.overflow = "hidden";
      if (scrollBarWidth > 0) {
        const currentPR = parseFloat(getComputedStyle(body).paddingRight) || 0;
        body.style.paddingRight = `${currentPR + scrollBarWidth}px`;
      }
    }

    return () => {
      // Restore styles
      body.style.overflow = prev.overflow;
      body.style.paddingRight = prev.paddingRight;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;

      // Put the page back where it was (for iOS fixed strategy)
      if (isiOS) {
        window.scrollTo(0, startScrollY);
      }
    };
  }, [enabled]);
}
